import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../interceptors";
import { baseUrl } from "../../../config/config";

const RECOMMENDATION_LABELS = {
  STRONG_YES: { label: "Strong yes", color: "bg-emerald-600 text-white" },
  YES:        { label: "Yes",        color: "bg-emerald-100 text-emerald-700" },
  NO:         { label: "No",         color: "bg-red-100 text-red-700" },
  STRONG_NO:  { label: "Strong no",  color: "bg-red-600 text-white" },
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString();
};

const parseRatings = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try { return JSON.parse(raw); } catch { return []; }
};

export default function FeedbackTab({ candidateId, jobId, candidateName }) {
  const nav = useNavigate();
  const [scorecards, setScorecards] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!candidateId) return;
    setLoading(true);
    const url = `${baseUrl}/job-posting/scorecards/by-candidate?candidateId=${candidateId}` +
                (jobId ? `&jobId=${jobId}` : "");
    axiosInstance
      .get(url)
      .then((r) => setScorecards(Array.isArray(r.data) ? r.data : []))
      .catch(() => setScorecards([]))
      .finally(() => setLoading(false));
  }, [candidateId, jobId]);

  const goSubmit = () => {
    const params = new URLSearchParams();
    if (candidateName) params.set("candidateName", candidateName);
    const qs = params.toString();
    const jid = jobId || 1;
    nav(`/admin/scorecard/${candidateId}/${jid}${qs ? `?${qs}` : ""}`);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-vf-base">Interviewer scorecards</h3>
          <p className="text-xs text-vf-muted mt-0.5">
            Human feedback from interviewers, alongside AI evaluation.
          </p>
        </div>
        <button
          onClick={goSubmit}
          className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs"
        >
          + Submit feedback
        </button>
      </div>

      {loading ? (
        <div className="text-xs text-vf-muted py-6 text-center">Loading scorecards…</div>
      ) : scorecards.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-xl py-10 text-center">
          <div className="text-sm text-vf-muted">No scorecards submitted yet.</div>
          <div className="text-xs text-vf-muted mt-1">
            Click <strong>+ Submit feedback</strong> to add the first one.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {scorecards.map((sc) => {
            const ratings = parseRatings(sc.attributeRatings);
            const rec = RECOMMENDATION_LABELS[sc.recommendation] || { label: sc.recommendation || "—", color: "bg-slate-100 text-slate-700" };
            return (
              <div key={sc.id} className="border border-slate-200 rounded-xl p-4 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs text-vf-muted">
                    Submitted by user #{sc.submittedBy} · {fmtDate(sc.submittedAt || sc.createdAt)}
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${rec.color}`}>
                    {rec.label}
                  </span>
                </div>

                {ratings.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                    {ratings.map((r, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <span className="text-vf-base">{r.attributeName}</span>
                        <span className="font-semibold text-vf-primary">{r.rating || "—"}/5</span>
                      </div>
                    ))}
                  </div>
                )}

                {(sc.strengths || sc.concerns) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {sc.strengths && (
                      <div>
                        <div className="text-xs font-medium text-emerald-700 mb-0.5">Strengths</div>
                        <p className="text-vf-base whitespace-pre-wrap">{sc.strengths}</p>
                      </div>
                    )}
                    {sc.concerns && (
                      <div>
                        <div className="text-xs font-medium text-red-700 mb-0.5">Concerns</div>
                        <p className="text-vf-base whitespace-pre-wrap">{sc.concerns}</p>
                      </div>
                    )}
                  </div>
                )}

                {sc.overallRating != null && (
                  <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-vf-muted">
                    Average rating: <span className="font-semibold text-vf-base">{sc.overallRating}/5</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
